// Copyright (c) 2020 embyt GmbH. All rights reserved.

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";
import { Link } from "react-router-dom";

import { Bus, IModule, IOutput, OutputCmd, OutputMode } from "shared/types/parameter";
import { ICloudParameter } from "shared/types/cloudtypes";
import { isSet, round } from "shared/utils/utils";
import { IStateDataOutput } from "states/datastates";
import { MainPanel } from "types";
import { IState } from "states/states";
import { ActionType, IAction, IActionChangeOutput, raiseAction } from "actions";
import {
  makeCommandSeqNr,
  tabClickHandler,
  getVirtualSiteSourceName,
  getModuleChannelName,
  renderVirtualSiteIcon,
} from "guiutils";

import OutputModel, { IOState } from "model/output";

import IconOff from "icons/iconOff";
import IconOffOverride from "icons/iconOffOverride";
import IconOn from "icons/iconOn";
import IconOnOverride from "icons/iconOnOverride";
import IconUnknown from "icons/iconUnknown";
import IconEdit from "icons/iconEdit";

interface IOutputExtProps {
  index: number;
  isEditable: boolean;
  showModule: boolean;
  isDashboard?: boolean;
}

interface IOutputProps {
  parameter: IOutput;
  value: IStateDataOutput;
  module: IModule | undefined;
  moduleStatus: boolean | undefined;
  showId: boolean;
  virtualSiteName: string | undefined;
}

const mapStateToProps = (state: IState, ownProps: IOutputExtProps) => {
  const parameter = state.para.parameter!.output[ownProps.index];
  const moduleIndex =
    parameter?.bus === Bus.MODULE ?
      state.para.parameter?.module?.findIndex((curItem) => curItem.id === parameter.module)
    : undefined;
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IOutputProps = {
    parameter,
    value: state.data.output[ownProps.index],
    module: isSet(moduleIndex) ? state.para.parameter?.module?.[moduleIndex] : undefined,
    moduleStatus: isSet(moduleIndex) ? state.data.module[moduleIndex] : true,
    showId: !ownProps.isDashboard && Boolean(curUser?.accessRights.setVirtualParameters),
    virtualSiteName:
      ownProps.isDashboard ? undefined : (
        getVirtualSiteSourceName(
          (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.output,
          ownProps.index,
          state.cached.selectedSite,
          state.site.siteList,
        )
      ),
  };
  return props;
};

interface IOutputDispatch {
  onEdit: () => void;
  onToggle: (cmd: OutputCmd) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IOutputExtProps,
) => {
  const dispatchProps: IOutputDispatch = {
    onEdit: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_OUTPUT, ownProps.index));
    },
    onToggle: (cmd: OutputCmd) => {
      const payload: IActionChangeOutput = {
        commandSeqNr: makeCommandSeqNr(),
        index: ownProps.index,
        cmd,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_OUTPUT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  edit: {
    id: "change value",
  },
  analog: {
    id: "analog",
  },
  pwm: {
    id: "control value",
  },
  on: {
    id: "On",
  },
  off: {
    id: "Off",
  },
  override: {
    id: "override",
  },
  ioType: {
    id: "Outputs",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class OutputComponent extends React.Component<IOutputProps & IOutputExtProps & IOutputDispatch> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const output = new OutputModel(this.props.parameter, this.props.value, this.props.moduleStatus);
    const hasValue = output.parameter.mode === OutputMode.ANALOG;
    const isEditable =
      this.props.isEditable &&
      output.parameter.webCtrl &&
      output.parameter.mode !== OutputMode.ANALOG &&
      !output.isOffline;

    return (
      <tr className={`outputitem ${output.isOffline ? "offline" : ""}`}>
        {this.props.showId && <td className="optional-md">{this.props.parameter.id}</td>}
        <td>
          {this.props.parameter.name}
          {this.props.virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(valueMessages.virtualSite, {
                site: this.props.virtualSiteName,
              }),
            )}
        </td>
        {this.props.showModule && (
          <td className="optional-sm">
            <Link to={`../${MainPanel.MODULES}`} style={{ textDecoration: "none" }}>
              {this.props.module?.name}
              {this.props.module &&
                ` - ${getModuleChannelName(this.props.module.type, this.props.parameter.channel, "output")}`}
            </Link>
          </td>
        )}
        <td
          className={`icon-col ${hasValue ? undefined : "right"} ${isEditable ? "img-link" : ""}`}
          colSpan={hasValue ? 1 : 2}
          title={this.getText(output)}
          onClick={
            isEditable && !output.isOffline ?
              () => {
                const cmd =
                  output.isOverride ?
                    output.state === IOState.ON ?
                      OutputCmd.OVERRIDE_OFF
                    : OutputCmd.OVERRIDE_ON
                  : output.state === IOState.OFF ? OutputCmd.ON
                  : OutputCmd.OFF;
                this.props.onToggle(cmd);
              }
            : undefined
          }
        >
          {this.getIcon(output)}
        </td>
        {hasValue && (
          <td className="value-col">
            {output.parameter.mode === OutputMode.ANALOG && output.getText()}
          </td>
        )}
        {isEditable ?
          <td
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={this.props.onEdit}
            className="edit-col img-link"
            title={this.context.formatMessage(messages.edit)}
          >
            <IconEdit />
          </td>
        : <td className="edit-col"></td>}
      </tr>
    );
  }

  private getIcon(output: OutputModel) {
    if (output.parameter.mode === OutputMode.ANALOG) return <></>;
    if (output.state === IOState.OFF) return output.isOverride ? <IconOffOverride /> : <IconOff />;
    if (output.state === IOState.ON) return output.isOverride ? <IconOnOverride /> : <IconOn />;
    return <IconUnknown />;
  }

  private getText(output: OutputModel) {
    if (output.parameter.mode === OutputMode.ANALOG)
      return this.context.formatMessage(messages.analog);
    if (output.parameter.mode === OutputMode.PWM)
      return `${this.context.formatMessage(messages.pwm)}: ${round(output.value?.logical)}`;
    const content: string[] = [];
    if (output.isOverride) content.push(this.context.formatMessage(messages.override));
    if (output.state === IOState.OFF) content.push(this.context.formatMessage(messages.off));
    if (output.state === IOState.ON) content.push(this.context.formatMessage(messages.on));
    return content.join(" ");
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OutputComponent);
